import {AppAccessStatus} from "../shared/enums/appAccessStatus";
import {SortOrder} from "../shared/enums/sortOrder";
import ResidentDetail from "../stores/domainStore/pages/models/residentDetail";
class ResidentRefiner {
  /**
   * Refines a list of residents based on the provided options.
   *
   * @param residents - The array of ResidentDetail objects to be refined.
   * @param pageNumber - The page number to fetch, starting from 1.
   * @param sizePerPage - The number of residents to display per page.
   * @param sortField - (Optional) The property name to sort the residents by.
   * @param sortOrder - (Optional) The sorting order, either 'asc' for ascending or 'desc' for descending.
   * @param searchTerm - (Optional) The search term to filter residents by. It will be matched against all properties of the residents.
   * @returns An array of ResidentDetail objects that meet the specified criteria (pagination, sorting, and search).
   */
  static refine(
    residents: ResidentDetail[],
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder,
    searchTerm?: string | undefined,
    appAccessStatus?: AppAccessStatus | undefined
  ): ResidentDetail[] {
    // Filter first
    residents = ResidentRefiner.filter(residents, searchTerm, appAccessStatus);

    // Sort
    residents = ResidentRefiner.sort(residents, sortField, sortOrder);

    // Paginate
    return ResidentRefiner.paginate(residents, pageNumber, sizePerPage);
  }

  static trimmedSearchTerm(searchTerm: string | undefined): string | undefined {
    if (!searchTerm) return searchTerm;

    return searchTerm.trim();
  }

  static filter(
    residents: ResidentDetail[],
    searchTerm: string | undefined,
    appAccessStatus?: AppAccessStatus | undefined
  ): ResidentDetail[] {
    // Clone the original array to avoid modifying the source data
    let residentsCopy = [...residents];

    // Filter by search term
    residentsCopy = ResidentRefiner.search(residentsCopy, searchTerm);

    // Filter by status
    return ResidentRefiner.filterByAppAccessStatus(
      residentsCopy,
      appAccessStatus
    );
  }

  /**
   * Filters a list of residents based on the provided search term.
   * The search term is applied according to the following conditions:
   * 1. The search is case-insensitive and covers all resident properties.
   * 2. If the search term includes a space between two words, it matches first and last names. For example, 'j d' matches 'John Doe' and 'Joe Doe'.
   * 3. It matches unit codes if the search term appears in any part of the unit code. For instance, 'T150' matches 'CUTH150'.
   */
  static search(
    residents: ResidentDetail[],
    searchTerm: string | undefined
  ): ResidentDetail[] {
    // Clone the original array to avoid modifying the source data
    const residentsCopy = [...residents];

    if (!searchTerm) return residentsCopy;

    const trimmedSearchTerm = this.trimmedSearchTerm(searchTerm);

    //split the trimmedSearchTerm into words only for the first space character
    const searchWords = trimmedSearchTerm?.split(" ");

    //only search the visible properties
    const propertiesToSearch = [
      "firstName",
      "lastName",
      "unitCode",
      "preferredName",
      "appAccountEmail",
      "emailAddress"
    ];

    return residentsCopy.filter((resident) => {
      const matchedProperties = propertiesToSearch.filter((property) => {
        const propertyValue = resident[property as keyof ResidentDetail];
        if (propertyValue && typeof propertyValue === "string") {
          return propertyValue
            .toLocaleLowerCase()
            .startsWith(trimmedSearchTerm!.toLocaleLowerCase());
        }
        return false;
      });

      const matchedNames = ResidentRefiner.matchNames(
        searchWords,
        resident.firstName,
        resident.lastName
      );

      const matchedUnitCode = ResidentRefiner.matchUnitCode(
        searchTerm,
        resident.unitCode
      );

      return matchedProperties.length > 0 || matchedNames || matchedUnitCode;
    });
  }

  static matchNames(
    searchWords: string[] | undefined,
    firstName: string,
    lastName: string
  ): boolean {
    if (!searchWords || searchWords?.length <= 1) return false;
    if (!firstName || !lastName) return false;

    const firstNameMatch = firstName
      .toLowerCase()
      .startsWith(searchWords[0].toLowerCase());
    const lastNameMatch = lastName
      .toLowerCase()
      .startsWith(searchWords[1].toLowerCase());
    return firstNameMatch && lastNameMatch;
  }

  static matchUnitCode(
    searchTerm: string | undefined,
    unitCode: string
  ): boolean {
    if (!searchTerm || !unitCode) return false;

    return unitCode.toLowerCase().includes(searchTerm.toLowerCase());
  }

  static filterByAppAccessStatus(
    residents: ResidentDetail[],
    appAccessStatus: AppAccessStatus | undefined
  ): ResidentDetail[] {
    // Clone the original array to avoid modifying the source data
    const residentsCopy = [...residents];

    if (appAccessStatus === undefined) return residentsCopy;

    if (appAccessStatus === AppAccessStatus.Access)
      return residentsCopy.filter(
        (resident) =>
          resident.appAccessStatus === AppAccessStatus.Access ||
          resident.appAccessStatus === AppAccessStatus.Active
      );

    return residents.filter(
      (resident) => resident.appAccessStatus === appAccessStatus
    );
  }

  static sort(
    residents: ResidentDetail[],
    sortField: string | undefined,
    sortOrder: SortOrder | undefined
  ): ResidentDetail[] {
    // Clone the original array to avoid modifying the source data
    const residentsCopy = [...residents];

    if (!sortField || !sortOrder) return residentsCopy;

    // Sort the residents based on the sorting options
    residentsCopy.sort((a, b) => {
      // Sort the residents appAccessStatus which is enum, by the sorting options
      if (sortField === "appAccessStatus") {
        const aValue = ResidentRefiner.getAppAccessStatusOrderValue(
          a[sortField as keyof ResidentDetail] as AppAccessStatus
        );
        const bValue = ResidentRefiner.getAppAccessStatusOrderValue(
          b[sortField as keyof ResidentDetail] as AppAccessStatus
        );
        if (sortOrder === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        const aValue = a[sortField as keyof ResidentDetail] as string;
        const bValue = b[sortField as keyof ResidentDetail] as string;

        if (!aValue) {
          return 1;
        }

        if (!bValue) {
          return -1;
        }

        if (aValue === bValue) {
          return 0;
        }

        // Sort both alphabetical and numeric values
        // Unit Code is a mix of alphabetical and numeric values
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue, "en", {numeric: true})
          : bValue.localeCompare(aValue, "en", {numeric: true});
      }
    });
    return residentsCopy;
  }

  static getAppAccessStatusOrderValue(
    appAccessStatus: AppAccessStatus
  ): number {
    switch (appAccessStatus) {
      case AppAccessStatus.Active:
        return 3;
      case AppAccessStatus.Access:
        return 1;
      case AppAccessStatus.InActive:
        return 4;
      case AppAccessStatus.RequiresAction: // Displays as "Action" so put second in alphabetical order
        return 2;
    }
  }

  static paginate(
    residents: ResidentDetail[],
    pageNumber: number,
    sizePerPage: number
  ): ResidentDetail[] {
    // Clone the original array to avoid modifying the source data
    const residentsCopy = [...residents];

    const startIndex = (pageNumber - 1) * sizePerPage;
    const endIndex = startIndex + sizePerPage;

    // Return the residents for the specified page
    return residentsCopy.slice(startIndex, endIndex);
  }
}

export {ResidentRefiner};
